import { ref, watchEffect } from "vue";
import KeenSlider, { KeenSliderInstance } from "keen-slider";
import useCustomNavigationPlugin from "@/shared/keen-slider/plugins/keen-custom-navigation-plugin";
import { DsButtonRefType } from "@sazka/design-system/src/button/ds-button-reference";
import { useBreakpoints } from "@sazka/utils/composables/use-breakpoints";
import { onViewportEnter } from "@sazka/utils/viewport-enter";

const useGamesThemeGamesCarousel = (carouselWrapper: HTMLElement) => {
    const isSm = useBreakpoints("sm");
    const isSm2 = useBreakpoints("sm2");

    const prevButton = ref<DsButtonRefType>(
        carouselWrapper.querySelector<HTMLButtonElement>(".js-games-theme-games-prev")
    );
    const dots = ref<HTMLElement>(carouselWrapper.querySelector<HTMLElement>(".js-games-theme-games-dots"));
    const nextButton = ref<DsButtonRefType>(
        carouselWrapper.querySelector<HTMLButtonElement>(".js-games-theme-games-next")
    );

    const compactDesktopRoot = carouselWrapper.querySelector<HTMLElement>(".games-theme-games--desktop");
    const compactMobileRoot = carouselWrapper.querySelector<HTMLElement>(".games-theme-games--mobile");
    const defaultRoot = carouselWrapper.querySelector<HTMLElement>(".games-theme-games--default");

    let sliderInstances: KeenSliderInstance[] = [];

    const resetElementStyles = (element: HTMLElement) => {
        if (!element) return;
        element.style.gap = null;
        element.style.minWidth = null;
        element.style.maxWidth = null;
        element.style.transform = null;
    };

    const initSlider = () => {
        sliderInstances.forEach((instance) => {
            if (instance) {
                instance.destroy();
            }
        });
        sliderInstances = [];

        if (compactDesktopRoot) resetElementStyles(compactDesktopRoot);
        if (compactMobileRoot) resetElementStyles(compactMobileRoot);
        if (defaultRoot) resetElementStyles(defaultRoot);

        if (isSm.value) {
            if (compactDesktopRoot) {
                compactDesktopRoot.style.gap = "0";
                compactMobileRoot.style.gap = "0";
                if (!isSm2.value) {
                    const mobileSlider = new KeenSlider(
                        compactMobileRoot,
                        {
                            selector: ".games-theme-games-item-small",
                            slides: { perView: 1, spacing: 12 },
                        },
                        [useCustomNavigationPlugin(prevButton, nextButton, dots)]
                    );
                    sliderInstances.push(mobileSlider);
                } else {
                    const desktopSlider = new KeenSlider(
                        compactDesktopRoot,
                        {
                            selector: ".games-theme-games-item-small",
                            slides: { perView: 2, spacing: 16 },
                        },
                        [useCustomNavigationPlugin(prevButton, nextButton, dots)]
                    );
                    sliderInstances.push(desktopSlider);
                }
            } else if (defaultRoot) {
                defaultRoot.style.gap = "0";
                const defaultSlider = new KeenSlider(
                    defaultRoot,
                    {
                        slides: {
                            perView: "auto",
                            spacing: 12,
                        },
                        selector: ".games-theme-games-item",
                        breakpoints: {
                            "(min-width: 1024px)": {
                                slides: { perView: "auto", spacing: 16 },
                            },
                        },
                    },
                    [useCustomNavigationPlugin(prevButton, nextButton, dots)]
                );
                sliderInstances.push(defaultSlider);
            }
        }
    };

    watchEffect(() => {
        initSlider();
    });
};

document.ready(() => {
    const carousels = document.querySelectorAll<HTMLDivElement>(".games-theme-games__wrapper");

    carousels.forEach((root) => {
        onViewportEnter(root, 0, "400px").then(() => {
            useGamesThemeGamesCarousel(root);
        });
    });
});
